import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { api } from "../../lib/api";
import { useSEO } from "../../hooks/useSEO";
import { useCompany } from "../../context/CompanyContext";
import { fmtTRY } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";
import RecentlyViewed, { pushRecentlyViewed } from "../../components/public/RecentlyViewed";
import FrequentlyBoughtTogether from "../../components/public/FrequentlyBoughtTogether";
import ShareBar from "../../components/public/ShareBar";
import {
  ShoppingCart, Package, Truck, Shield, ArrowLeft, Store, Clock, Star, Send, MessageCircle,
  ZoomIn, X as XIcon, ChevronLeft, ChevronRight,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const track = (event, product_id) => { try { axios.post(`${BACKEND}/api/shop/analytics/track`, { event, product_id }); } catch {} };

export default function ProductDetail() {
  const { sku } = useParams();
  const { add } = useCart();
  const { company } = useCompany();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ reviewer_name: "", rating: 5, comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shop/products/${sku}`);
        setP(data);
        setActiveImg(0);
        track("view", data.id);
        // iter148.1 — Son baktıklara ekle
        try { pushRecentlyViewed(data); } catch { /* ignore */ }
        const rev = await api.get(`/shop/reviews/${data.id}`).catch(() => ({ data: [] }));
        setReviews(rev.data || []);
      } catch { setNotFound(true); }
    })();
  }, [sku]);

  // iter127 — Product JSON-LD schema (Google Rich Results)
  // NOTE: useSEO MUST be called unconditionally (before any early return) — React rules-of-hooks.
  useSEO((() => {
    if (!p) return {};
    const inStock = p.in_stock ?? ((p.stock_qty ?? p.quantity ?? 0) > 0);
    const displayPrice = p.price ?? p.unit_price ?? 0;
    const gallery = (Array.isArray(p.images) && p.images.length > 0) ? p.images : (p.image ? [p.image] : []);
    return {
      title: `${p.name}${p.brand_name ? ` — ${p.brand_name}` : ""} | Adana Seramik & Yapı Malzemeleri`,
      description: (p.description || `${p.name} ürününü Bordo Seramik'in Adana Çukurova, Sarıçam ve Seyhan mağazalarında inceleyin. ${inStock ? "Stokta var" : "Sipariş üzerine"} — hızlı teslimat, uygun fiyat.`).slice(0, 160),
      keywords: [p.name, p.brand_name, p.category_name, "adana seramik", "seramik satın al"].filter(Boolean).join(", "),
      canonical: `/urun/${p.sku || p.code}`,
      image: gallery[0],
      ogType: "product",
      breadcrumbs: [
        { name: "Anasayfa", path: "/" },
        { name: "Ürünler", path: "/urunler" },
        ...(p.category_name ? [{ name: p.category_name, path: `/urunler?kategori=${p.category_slug || ""}` }] : []),
        { name: p.name, path: `/urun/${p.sku || p.code}` },
      ],
      jsonld: {
        "@context": "https://schema.org",
        "@type": "Product",
        name: p.name,
        description: p.description || `${p.name} — Bordo Seramik`,
        sku: p.sku || p.code,
        mpn: p.code || p.sku,
        image: gallery.length > 0 ? gallery : undefined,
        brand: p.brand_name ? { "@type": "Brand", name: p.brand_name } : undefined,
        category: p.category_name || undefined,
        offers: {
          "@type": "Offer",
          priceCurrency: "TRY",
          price: displayPrice,
          availability: inStock ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
          itemCondition: "https://schema.org/NewCondition",
          url: `${window.location.origin}/urun/${p.sku || p.code}`,
          seller: { "@type": "Organization", name: "Bordo Seramik" },
        },
        aggregateRating: p.rating_count > 0 ? {
          "@type": "AggregateRating",
          ratingValue: p.rating_avg,
          reviewCount: p.rating_count,
        } : undefined,
        review: reviews.slice(0, 5).map((rv) => ({
          "@type": "Review",
          author: { "@type": "Person", name: rv.reviewer_name || "Müşteri" },
          reviewRating: { "@type": "Rating", ratingValue: rv.rating, bestRating: 5 },
          reviewBody: rv.comment,
          datePublished: rv.created_at,
        })),
      },
    };
  })());

  if (notFound) return (
    <div className="max-w-3xl mx-auto p-16 text-center">
      <h1 className="font-display font-bold text-2xl text-zinc-900">Ürün bulunamadı</h1>
      <Link to="/urunler" className="text-primary hover:underline text-sm mt-4 inline-block">← Ürünlere Dön</Link>
    </div>
  );
  if (!p) return <div className="p-16 text-center text-zinc-500">Yükleniyor...</div>;

  const inStock = p.in_stock ?? ((p.stock_qty ?? p.quantity ?? 0) > 0);
  const displayPrice = p.price ?? p.unit_price ?? 0;
  const handleAdd = () => {
    add({ ...p, unit_price: displayPrice }, Number(qty));
    toast.success(inStock ? `${p.name} sepete eklendi` : `${p.name} sepete eklendi (siparişle getirtilir)`);
    track("add_to_cart", p.id);
  };

  const submitReview = async () => {
    if (!reviewForm.reviewer_name.trim()) return toast.error("İsim gerekli");
    if (!reviewForm.rating) return toast.error("Puan seçin");
    setReviewSubmitting(true);
    try {
      const { data } = await api.post(`/shop/reviews/${p.id}`, reviewForm);
      setReviews((r) => [data, ...r]);
      setReviewForm({ reviewer_name: "", rating: 5, comment: "" });
      toast.success("Yorumunuz kaydedildi, teşekkürler!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
    setReviewSubmitting(false);
  };

  const attrs = (p.attribute_values || []).filter((a) => (a.value || "").toString().trim());

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Link to="/urunler" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-primary mb-6"><ArrowLeft size={14}/> Ürünlere Dön</Link>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-3">
          {(() => {
            const gallery = (Array.isArray(p.images) && p.images.length > 0) ? p.images : (p.image ? [p.image] : []);
            const current = gallery[activeImg] || gallery[0];
            return (
              <>
                <div className="aspect-square bg-zinc-50 rounded-lg flex items-center justify-center overflow-hidden relative group cursor-zoom-in" data-testid="pd-main-image" onClick={() => current && setZoomOpen(true)}>
                  {current ? <img src={current} alt={p.name} loading="eager" fetchpriority="high" decoding="async" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" /> : <Package size={80} className="text-zinc-300" />}
                  {current && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setZoomOpen(true); }}
                      className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm border border-zinc-200 shadow-md flex items-center justify-center hover:bg-white hover:shadow-lg transition-all opacity-0 group-hover:opacity-100"
                      aria-label="Yakınlaştır"
                      data-testid="pd-zoom-btn"
                    >
                      <ZoomIn size={18} className="text-zinc-700"/>
                    </button>
                  )}
                  <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                    {p.in_showroom && (
                      <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded shadow" data-testid="pd-showroom-badge">
                        <Store size={12}/> Mağazamızda Teşhirde
                      </span>
                    )}
                    {p.is_featured && (
                      <span className="inline-flex items-center gap-1 bg-primary text-white text-xs font-bold px-2.5 py-1 rounded shadow" data-testid="pd-featured-badge">
                        ★ Öne Çıkan
                      </span>
                    )}
                  </div>
                </div>
                {gallery.length > 1 && (
                  <div className="grid grid-cols-5 gap-2" data-testid="pd-thumbs">
                    {gallery.map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveImg(i)}
                        className={`aspect-square rounded border-2 overflow-hidden bg-zinc-50 transition-all ${i === activeImg ? "border-primary shadow-sm" : "border-zinc-200 hover:border-zinc-400 opacity-80 hover:opacity-100"}`}
                        data-testid={`pd-thumb-${i}`}
                      >
                        <img src={src} alt={`${p.name} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover"/>
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        <div>
          {/* iter95 — Marka logolu şerit */}
          <div className="flex items-center gap-3 mb-3">
            {p.brand_logo && <img src={p.brand_logo} alt={p.brand_name} className="h-10 max-w-[120px] object-contain" data-testid="pd-brand-logo"/>}
            {p.brand_name && <div className="text-xs uppercase tracking-wide text-primary font-bold">{p.brand_name}</div>}
          </div>
          <h1 className="font-display font-bold text-3xl text-zinc-900 mb-2">{p.name}</h1>
          <div className="text-sm text-zinc-500 font-mono mb-4">Kod: {p.sku || p.code}</div>

          {p.rating_count > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(n => (
                  <Star key={n} size={16} className={n <= Math.round(p.rating_avg) ? "fill-amber-500 text-amber-500" : "text-zinc-300"}/>
                ))}
              </div>
              <span className="text-sm text-zinc-600">{p.rating_avg?.toFixed(1)} <span className="text-zinc-400">({p.rating_count} yorum)</span></span>
            </div>
          )}

          <div className="flex items-baseline gap-3 mb-4">
            {displayPrice > 0
              ? <>
                  <div className="text-4xl font-bold text-primary tabular-nums" data-testid="pd-price">{fmtTRY(displayPrice)}</div>
                  <div className="text-xs text-zinc-500">KDV Dahil</div>
                </>
              : <div className="text-xl font-semibold text-zinc-400">Fiyat için iletişime geçin</div>}
          </div>

          <div className="flex items-center gap-2 text-sm mb-4">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${inStock ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}
              data-testid="pd-stock-status">
              {inStock ? <>● Stokta Var</> : <><Clock size={12}/> Siparişle Getirtilir</>}
            </span>
            {!inStock && p.lead_days && (
              <span className="text-xs text-zinc-500">Ortalama termin: <strong>{p.lead_days} gün</strong></span>
            )}
          </div>

          {p.description && <p className="text-zinc-700 mb-6 leading-relaxed">{p.description}</p>}

          {/* iter95 — Ürün özellikleri chip */}
          {attrs.length > 0 && (
            <div className="mb-6">
              <div className="text-xs font-semibold uppercase text-zinc-500 mb-2">Özellikler</div>
              <div className="flex flex-wrap gap-1.5">
                {attrs.map((a, i) => (
                  <span key={i} className="text-xs border border-zinc-200 bg-zinc-50 rounded px-2 py-1">
                    <span className="text-zinc-500">{a.name}:</span> <strong>{a.value}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-20" data-testid="pd-qty" />
            <Button onClick={handleAdd} size="lg" className="flex-1 bg-primary hover:bg-primary/90" data-testid="pd-add-cart">
              <ShoppingCart size={18} className="mr-2" /> {inStock ? "Sepete Ekle" : "Siparişle Getirt"}
            </Button>
          </div>

          {/* iter133 — Ürün-özel WhatsApp destek butonu */}
          {(company?.whatsapp_number || company?.phone) && (() => {
            const raw = (company.whatsapp_number || company.phone || "").replace(/[^0-9]/g, "");
            const wa = raw.startsWith("90") ? raw : (raw.startsWith("0") ? "90" + raw.slice(1) : "90" + raw);
            const msg = encodeURIComponent(`Merhaba, ${p.name} (${p.sku || p.code}) ürünü hakkında sorum var. Ürün linki: ${window.location.href}`);
            return (
              <a href={`https://wa.me/${wa}?text=${msg}`} target="_blank" rel="noopener noreferrer"
                 data-testid="pd-whatsapp-inquire"
                 className="w-full mb-4 flex items-center justify-center gap-2 py-3 rounded-lg bg-[#25D366] hover:bg-[#20b757] text-white font-semibold text-sm transition-colors">
                <MessageCircle size={18}/> Bu ürünle ilgili sorum var
              </a>
            );
          })()}

          {/* iter164 — Ürünü Paylaş */}
          <ShareBar product={{ ...p, price: displayPrice }} url={typeof window !== "undefined" ? window.location.href : `https://bordoseramik.com/urun/${p.sku || p.code}`}/>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2 p-3 bg-zinc-50 rounded"><Truck size={16} className="text-primary" /><span>Türkiye geneli kargo</span></div>
            <div className="flex items-center gap-2 p-3 bg-zinc-50 rounded"><Shield size={16} className="text-primary" /><span>Güvenli ödeme</span></div>
          </div>
        </div>
      </div>

      {/* iter148.2 — Sık Beraber Alınanlar */}
      <div className="max-w-6xl mx-auto px-4">
        <FrequentlyBoughtTogether mainProduct={p}/>
      </div>

      {/* iter148.1 — Son Baktıklarınız */}
      <RecentlyViewed excludeId={p.id}/>

      {/* iter95 — Yorumlar */}
      <div className="mt-12 border-t border-zinc-200 pt-8">
        <h2 className="font-display font-bold text-2xl text-zinc-900 mb-4">Yorumlar & Puanlar</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-3">
            {reviews.length === 0 && <div className="text-sm text-zinc-500 italic p-6 bg-zinc-50 rounded">Henüz yorum yok — ilk yorumu siz yazın!</div>}
            {reviews.map((r) => (
              <div key={r.id} className="bg-white border border-zinc-200 rounded p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">{r.reviewer_name}</div>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(n => <Star key={n} size={12} className={n <= r.rating ? "fill-amber-500 text-amber-500" : "text-zinc-300"}/>)}
                  </div>
                </div>
                {r.comment && <div className="text-sm text-zinc-700 mt-1">{r.comment}</div>}
                <div className="text-[10px] text-zinc-400 mt-1">{new Date(r.created_at).toLocaleDateString("tr-TR")}</div>
              </div>
            ))}
          </div>
          <div className="bg-zinc-50 border border-zinc-200 rounded p-4">
            <div className="font-semibold text-sm mb-3">Yorum Yaz</div>
            <div className="space-y-2">
              <Input placeholder="Adınız *" value={reviewForm.reviewer_name} onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })} data-testid="review-name"/>
              <div className="flex items-center gap-1" data-testid="review-rating">
                {[1,2,3,4,5].map(n => (
                  <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} type="button">
                    <Star size={22} className={n <= reviewForm.rating ? "fill-amber-500 text-amber-500" : "text-zinc-300 hover:text-amber-300"}/>
                  </button>
                ))}
              </div>
              <textarea rows={3} placeholder="Yorumunuz (opsiyonel)" value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        className="w-full px-3 py-2 border border-zinc-200 rounded text-sm" data-testid="review-comment"/>
              <Button onClick={submitReview} disabled={reviewSubmitting} className="w-full bg-primary hover:bg-primary/90" data-testid="review-submit">
                <Send size={14} className="mr-1"/> {reviewSubmitting ? "Gönderiliyor..." : "Gönder"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* iter148.1 — Fullscreen Zoom Modal */}
      {zoomOpen && (() => {
        const gallery = (Array.isArray(p.images) && p.images.length > 0) ? p.images : (p.image ? [p.image] : []);
        const current = gallery[activeImg] || gallery[0];
        return (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center animate-in fade-in"
            data-testid="pd-zoom-modal"
            onClick={() => setZoomOpen(false)}
          >
            <button
              type="button"
              onClick={() => setZoomOpen(false)}
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 z-10"
              aria-label="Kapat"
              data-testid="pd-zoom-close"
            >
              <XIcon size={22}/>
            </button>
            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + gallery.length) % gallery.length); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 z-10"
                  aria-label="Önceki"
                  data-testid="pd-zoom-prev"
                >
                  <ChevronLeft size={22}/>
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % gallery.length); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 z-10"
                  aria-label="Sonraki"
                  data-testid="pd-zoom-next"
                >
                  <ChevronRight size={22}/>
                </button>
              </>
            )}
            <div className="relative w-full max-w-6xl max-h-[90vh] flex items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
              {current && (
                <img
                  src={current}
                  alt={p.name}
                  className="max-w-full max-h-[90vh] object-contain select-none cursor-move"
                  style={{ transition: "transform 0.3s" }}
                  draggable={false}
                  data-testid="pd-zoom-img"
                />
              )}
            </div>
            {gallery.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 max-w-full overflow-x-auto px-4" data-testid="pd-zoom-thumbs">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setActiveImg(i); }}
                    className={`w-14 h-14 rounded border-2 overflow-hidden bg-black/40 shrink-0 transition-all ${i === activeImg ? "border-white" : "border-white/20 hover:border-white/60 opacity-60 hover:opacity-100"}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
            <div className="absolute top-4 left-4 text-white/70 text-xs" data-testid="pd-zoom-counter">
              {activeImg + 1} / {gallery.length}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
